import React, { useEffect } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import SuspanceLoading from "../components/SuspanceLoading";
import { useSelector, useDispatch } from "react-redux";
import { useSubscription } from "../components/Auth/SubscriptionContext";
import { routes } from "./Routes";
import {
  clearAuthSession,
  hasCommittedAuthArtifacts,
} from "../redux/Apps/users/authSession";
import { persistor } from "../redux/store";

const ALWAYS_ALLOWED_PROTECTED_PATHS = new Set([
  "/dashboard/settings",
  "/dashboard/settings/*",
]);

const ProtectedRoutes = () => {
  const { token } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const isAuthenticated = !!(
    token &&
    typeof token === "string" &&
    token.trim() !== ""
  );
  const location = useLocation();

  const {
    hasPath,
    hasActivePlan,
    ready: subscriptionReady,
    subscriptionFetched,
  } = useSubscription();

  useEffect(() => {
    if (!isAuthenticated && hasCommittedAuthArtifacts()) {
      clearAuthSession({
        dispatch,
        purgePersistor: () => persistor.purge(),
        clearAllStorage: true,
      });
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (location.pathname === "/dashboard") {
      localStorage.removeItem("setLaterRes");
    }
  }, [location]);

  const authenticatedFallbackPath =
    isAuthenticated && !hasPath("/dashboard")
      ? "/dashboard/settings/subscription-billing"
      : "/dashboard";

  const shouldWaitForSubscription =
    isAuthenticated &&
    location.pathname.startsWith("/dashboard") &&
    (!subscriptionReady || !subscriptionFetched);

  if (shouldWaitForSubscription) {
    return <SuspanceLoading />;
  }

  return (
    <Routes>
      {/* Public routes - redirect to dashboard if authenticated */}
      {routes
        .filter((r) => !r.isProtected)
        .map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              isAuthenticated && route.redirectIfAuthenticated !== false ? (
                <Navigate to={authenticatedFallbackPath} replace />
              ) : (
                route.element
              )
            }
          />
        ))}

      {/* Protected routes - redirect to login if not authenticated */}
      {routes
        .filter((r) => r.isProtected)
        .map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              !isAuthenticated ? (
                <Navigate to="/login" replace />
              ) : ALWAYS_ALLOWED_PROTECTED_PATHS.has(route.path) ? (
                route.element
              ) : hasActivePlan() ? (
                route.element
              ) : hasPath(route.path) ? (
                route.element
              ) : (
                <Navigate to={authenticatedFallbackPath} replace />
              )
            }
          />
        ))}

      {/* Catch-all for unknown routes */}
      <Route
        path="*"
        element={
          <Navigate
            to={isAuthenticated ? authenticatedFallbackPath : "/login"}
            replace
          />
        }
      />
    </Routes>
  );
};

export default ProtectedRoutes;
